import { useState } from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export const CTASection = () => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.15 });
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        "https://script.google.com/macros/s/AKfycbySljjt9o04fwQuniP5tydhXw2RSxhyxtn7q1tBnSsgSw79jAjwEiAB0kuTKpdfqp0UNg/exec",
        {
          method: "POST",
          body: JSON.stringify({ email }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Dark indigo background */}
      <div className="absolute inset-0 bg-[#1E1B4B]" />
      {/* Animated decorative orbs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/15 rounded-full blur-[150px] pointer-events-none orb-float-1" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-violet-600/10 rounded-full blur-[120px] pointer-events-none orb-float-2" />
      {/* Grain overlay for depth */}
      <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWx0ZXI9InVybCgjYSkiIG9wYWNpdHk9IjEiLz48L3N2Zz4=')]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={ref}
          className={`max-w-2xl mx-auto text-center transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 mb-8 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}>
            <Sparkles size={14} className="text-emerald-400" />
            <span className="text-xs font-semibold text-indigo-200 tracking-wide">
              Join the waitlist today
            </span>
          </div>

          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white transition-all duration-700 delay-100 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}>
            Ready to Fast-Forward
            <br />
            Your Tenders?
          </h2>
          <p className={`mt-6 text-lg text-indigo-200/80 leading-relaxed max-w-lg mx-auto font-body transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}>
            Join businesses across India who are winning more government
            contracts with less effort.
          </p>

          {submitted ? (
            <div className="mt-10 flex items-center justify-center gap-3 text-emerald-300 animate-fade-in">
              <CheckCircle2 size={24} />
              <span className="text-lg font-semibold">
                You're on the list! We'll be in touch soon.
              </span>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className={`mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto transition-all duration-700 delay-300 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <Input
                type="email"
                placeholder="Enter your work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-white/[0.12] border-white/20 text-white placeholder:text-indigo-200/60 rounded-lg focus:border-indigo-400 focus:ring-indigo-400/30 flex-1 backdrop-blur-sm shadow-inner shadow-white/5"
              />
              <Button
                type="submit"
                disabled={loading}
                className="btn-glow bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 h-12 rounded-lg shadow-none group whitespace-nowrap"
              >
                {loading ? (
                  <span className="animate-pulse">Joining...</span>
                ) : (
                  <>
                    Request Early Access
                    <ArrowRight size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </>
                )}
              </Button>
            </form>
          )}

          <p className={`mt-6 text-xs text-indigo-300/50 transition-all duration-700 delay-400 ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}>
            No spam, ever. We'll only reach out when your early access is ready.
          </p>
        </div>
      </div>
    </section>
  );
};
